import { times } from "@common/helpers/collections";
import { getMatchingIntegrationAccount } from "@common/helpers/financialAccount";
import {
    Invoice,
    InvoiceSource,
    InvoiceStats,
    InvoiceStatus,
} from "@common/types/invoice";
import { Counterparty } from "@common/types/counterparty";
import {
    FinancialAccount,
    FinancialAccountSourceType,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { getNativeInvoicePdfFileName as getNativeInvoicePdfFileNameFromNumber } from "@common/helpers/nativeInvoice";
import { getInvoiceExternalUrl } from "@common/helpers/domains/invoices/invoiceExternalUrl";
import { getBillcomBaseUrl } from "./billcom";

const STRIPE_INVOICES_BASE_URL = "https://dashboard.stripe.com/invoices";

/**
 * An account that can act as an invoice provider (and thus surface invoices).
 * Stripe and Bill.com always qualify; Mercury only via its checking account.
 * Shared by the page gate and the in-table connect empty state so "connected"
 * means the same thing everywhere.
 */
export const isInvoiceProviderAccount = (
    account: FinancialAccount,
): boolean => {
    if (account.sourceType === FinancialAccountSourceType.STRIPE) {
        return true;
    }
    if (account.sourceType === FinancialAccountSourceType.MERCURY) {
        return account.financialType === FinancialAccountType.CHECKING;
    }
    if (account.sourceType === FinancialAccountSourceType.BILLCOM) {
        return true;
    }
    return false;
};

export function getBillcomNewInvoiceUrl() {
    return `${getBillcomBaseUrl()}/neo/receivables/invoices/add`;
}

function getStripeAccountIdForFinancialAccount(account: FinancialAccount) {
    return getMatchingIntegrationAccount(account)?.sourceAccountId ?? undefined;
}

export function getStripeDashboardUrlForFinancialAccount(
    account: FinancialAccount,
) {
    const accountId = getStripeAccountIdForFinancialAccount(account);
    if (!accountId) {
        return undefined;
    }
    return `${STRIPE_INVOICES_BASE_URL}/create?account=${accountId}`;
}

export function getInvoiceUrl(invoice: Invoice) {
    return (
        getInvoiceExternalUrl({
            invoice,
            billcomBaseUrl: getBillcomBaseUrl(),
        }) || undefined
    );
}

export function getInvoiceUrlTitle(invoice: Invoice) {
    switch (invoice.source) {
        case InvoiceSource.STRIPE:
            return "View in Stripe";
        case InvoiceSource.MERCURY:
            return "View in Mercury";
        case InvoiceSource.BILLCOM:
            return "View in BILL";
        default:
            return "";
    }
}

export function getNativeInvoicePdfFileName(
    invoice: Pick<Invoice, "invoiceNumber">,
): string {
    return getNativeInvoicePdfFileNameFromNumber(invoice.invoiceNumber);
}

export const mockInvoiceStats: InvoiceStats = {
    draft: { count: 10, total: 500 },
    open: { count: 10, total: 2000 },
    paid: { count: 10, total: 1000 },
    void: { count: 0, total: 0 },
    uncollectable: { count: 0, total: 0 },
};

export function generateMockInvoices(num: number): Invoice[] {
    const statusValues = Object.values(InvoiceStatus);
    return times(num, (i) => {
        const status =
            statusValues[Math.floor(Math.random() * statusValues.length)];

        return {
            id: `inv_${i}`,
            createdAt: new Date(),
            updatedAt: new Date(),
            paidAt: new Date(),
            due: new Date(),
            dateOnly: null,
            paidDateOnly: null,
            dueDateOnly: null,
            status,
            externalStatus: status,
            counterparty: {
                name: "John Doe",
                email: "john@doe.com",
            } as Counterparty,
            total: 1000,
            subtotal: 1000,
            tax: 0,
            discount: 0,
            currency: "USD",
            description: `Invoice ${i}`,
            memo: null,
            source: InvoiceSource.MANUAL,
            sourceId: `inv_${i}`,
            isSubscription: false,
            entityId: 1,
            financialAccountId: 0,
            uncollectableAt: null,
            voidedAt: null,
            invoiceNumber: `INV-${i}`,
            invoiceDate: null,
            financialDocumentIds: [],
            collectPayment: false,
            paymentToken: null,
            isTaxPerLineItem: false,
            isDiscountPerLineItem: false,
            isDepositPerLineItem: false,
            termType: null,
        };
    });
}
